import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { api, formatApiError } from "../lib/api";

function getOrCreateSession() {
  let s = localStorage.getItem("coomflona_chat_session");
  if (!s) {
    s = `pub-${Math.random().toString(36).slice(2)}-${Date.now()}`;
    localStorage.setItem("coomflona_chat_session", s);
  }
  return s;
}

function formatReply(text) {
  // Convert **bold** and links into JSX
  if (!text) return null;
  const parts = [];
  const regex = /(\*\*[^*]+\*\*|https?:\/\/\S+|\/[a-z0-9/-]+)/g;
  let last = 0;
  let m;
  let key = 0;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) parts.push(<span key={key++}>{text.slice(last, m.index)}</span>);
    const token = m[0];
    if (token.startsWith("**") && token.endsWith("**")) {
      parts.push(<strong key={key++}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith("http")) {
      parts.push(
        <a key={key++} href={token} target="_blank" rel="noreferrer" className="underline text-copaiba-300 hover:text-copaiba-500">
          {token.replace(/^https?:\/\//, "").replace(/\/$/, "")}
        </a>
      );
    } else if (token.startsWith("/")) {
      parts.push(
        <a key={key++} href={token} className="underline text-copaiba-300 hover:text-copaiba-500">
          {token}
        </a>
      );
    }
    last = m.index + token.length;
  }
  if (last < text.length) parts.push(<span key={key++}>{text.slice(last)}</span>);
  return parts;
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Oi! Eu sou a **Anambé**, assistente da COOMFLONA. Posso te contar sobre a cooperativa, os produtos da Eco Loja (copaíba, andiroba, mel, móveis Anambé, artesanato) ou direcionar você para o WhatsApp. Para produtos da **Eco Loja / Movelaria Anambé**, fale com a gente em (93) 99117‑7997. O que você quer saber?",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionRef = useRef(getOrCreateSession());
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open, loading]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/chat", {
        message: text,
        session_id: sessionRef.current,
        mode: "public",
      });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (err) {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: `Desculpe, tive um problema agora. ${formatApiError(err)}` },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const suggestions = [
    "Qual a história da COOMFLONA?",
    "Quais óleos vegetais vocês vendem?",
    "Como funciona a Movelaria Anambé?",
    "Quero comprar um produto",
  ];

  return (
    <>
      {/* Floating button */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="chat-toggle-button"
        aria-label={open ? "Fechar assistente" : "Abrir assistente"}
        className={`fixed bottom-6 right-6 z-[60] flex items-center gap-2 px-5 py-4 rounded-full shadow-xl transition-all duration-300 ${
          open ? "bg-forest-900 text-cream-100" : "bg-forest-700 text-cream-100 hover:bg-forest-900"
        }`}
      >
        {open ? <X className="w-5 h-5" /> : <MessageCircle className="w-5 h-5" />}
        <span className="hidden sm:inline text-sm font-medium">
          {open ? "Fechar" : "Falar com a Anambé"}
        </span>
      </button>

      {/* Panel */}
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-24 right-6 z-[60] w-[360px] sm:w-[400px] max-w-[calc(100vw-3rem)] h-[560px] max-h-[calc(100vh-8rem)] bg-cream-100 border border-forest-700/15 shadow-2xl flex flex-col overflow-hidden animate-fade-up"
        >
          {/* Header */}
          <div className="bg-forest-900 text-cream-100 px-5 py-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-copaiba-500 text-forest-900 flex items-center justify-center">
              <Sparkles className="w-4 h-4" />
            </div>
            <div className="flex-1">
              <div className="font-display text-lg leading-tight">Anambé</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-cream-100/70">
                Assistente COOMFLONA · GPT-5.2
              </div>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-cream-100">
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                data-testid={`chat-message-${m.role}-${i}`}
              >
                <div
                  className={`max-w-[88%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-forest-700 text-cream-100"
                      : "bg-white border border-cream-400 text-forest-900"
                  }`}
                >
                  {m.role === "assistant" ? formatReply(m.content) : m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-cream-400 text-forest-900 px-4 py-3 text-sm flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-forest-700 animate-pulse" />
                  <span className="w-2 h-2 rounded-full bg-forest-700 animate-pulse" style={{ animationDelay: "120ms" }} />
                  <span className="w-2 h-2 rounded-full bg-forest-700 animate-pulse" style={{ animationDelay: "240ms" }} />
                </div>
              </div>
            )}
          </div>

          {/* Suggestions (only shown initially) */}
          {messages.length <= 1 && !loading && (
            <div className="px-4 pb-3 flex flex-wrap gap-2 border-t border-cream-400 pt-3">
              {suggestions.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => {
                    setInput(s);
                    setTimeout(() => send({ preventDefault: () => {} }), 50);
                  }}
                  data-testid={`chat-suggestion-${s.slice(0, 12)}`}
                  className="text-xs px-3 py-1.5 bg-cream-200 border border-cream-400 hover:bg-forest-700 hover:text-cream-100 hover:border-forest-700 transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <form onSubmit={send} className="border-t border-cream-400 p-3 flex gap-2 bg-white">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Pergunte sobre a cooperativa ou os produtos…"
              data-testid="chat-input"
              maxLength={2000}
              disabled={loading}
              className="flex-1 bg-cream-100 border border-cream-400 focus:border-forest-700 focus:outline-none px-3 py-2.5 text-sm"
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              data-testid="chat-send-button"
              className="bg-forest-700 hover:bg-forest-900 disabled:opacity-50 text-cream-100 px-4 transition-colors"
              aria-label="Enviar"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
